import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDailyMenuApi } from "../api/DailyMenuApi";
import { useRecipeApi } from "../api/RecipeApi";
import type { Recipe } from "../types/Recipe";
import type { DailyMenu } from "../types/DailyMenu";
import { useDailyMenuRecipeApi } from "../api/DailyMenuRecipe";
import { useFontenehus } from "../context/FontenehusContext";
import { useUser } from "../context/UserContext";
import InfiniteSwiper from "../components/InfiniteSwiper";
import SuggestedLunches from "../components/DayDetailPage/SuggestComponent";
import DayNavigation from "../components/DayDetailPage/DayNavigation";
import RecipeList from "../components/DayDetailPage/RecipeList";
import AddRecipeSection from "../components/DayDetailPage/AddRecipeSection";
import MenuDetails from "../components/DayDetailPage/MenuDetails";
import ActionButtons from "../components/DayDetailPage/ActionButtons";
import type { InfiniteSwiperRef } from "../components/InfiniteSwiper";
import "./DayDetailsPage.css";
import { handleApiError } from "../utils/handleApiError";
import { useCategoryApi } from "../api/CategoryApi";
import { useIngredientApi } from "../api/IngredientApi";
import { useAllergenApi } from "../api/AllergenApi";
import RecipeDetail from "./RecipeDetailPage";
import { useModalContext } from "../context/ModalContext";
import { useRefresh } from "../context/RefreshContext ";
import { useConfirm } from "../context/ConfirmContext ";
import { toast } from "react-toastify";

const ANIMATION_DURATION = 300;

// Local item shape used by the swiper on mobile
type DayCardItem = {
  id: string; // unique key (we'll use the YYYY-MM-DD date)
  date: string; // YYYY-MM-DD
  menu: DailyMenu | null; // data for that day
};

const DayDetailsPage: React.FC = () => {
  const { user } = useUser();
  const { deleteDailyMenuRecipe } = useDailyMenuRecipeApi();
  const { getDailyMenuByDate, updateDailyMenu, getDailyMenusByRange } =
    useDailyMenuApi();
  const { selectedFontenehus, refreshTrigger } = useFontenehus();
  const { getRecipes } = useRecipeApi();
  const formatLocalDate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;
  const containerRef = useRef<HTMLDivElement | null>(null);

  const buildUpdateDto = (menu: DailyMenu, recipeIds: number[]) => ({
    fontenehusId: menu.fontenehusId,
    peopleAte: menu.peopleAte,
    portionsLeft: menu.portionsLeft,
    suggestedBy: menu.suggestedBy ?? "",
    notes: menu.notes ?? "",
    recipeIds,
  });

  //Modal stuff
  const { getCategories } = useCategoryApi();
  const { getAllergens } = useAllergenApi();
  const { getIngredients } = useIngredientApi();
  const [categories, setCategories] = useState<any[]>([]);
  const [allergens, setAllergens] = useState<any[]>([]);
  const [ingredients, setIngredients] = useState<any[]>([]);
  const { setIsAnyModalOpen } = useModalContext();

  // --- State ---
  const [editing, setEditing] = useState(false);
  const [editingMap, setEditingMap] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const raw = location.state?.selectedDate;
    return raw ? new Date(raw) : new Date(); // fallback = today
  });
  const confirm = useConfirm();
  const [showModalRecipeId, setShowModalRecipeId] = useState<
    number | undefined
  >(undefined);
  const { refreshKey } = useRefresh();
  const [menu, setMenu] = useState<DailyMenu | null>(null);
  const [allRecipes, setAllRecipes] = useState<Recipe[]>([]);
  const [tempMenu, setTempMenu] = useState<DailyMenu | null>(null);
  const [selectedRecipeIds, setSelectedRecipeIds] = useState<
    Record<string, number | "">
  >({});
  const [error, setError] = useState<string | null>(null);
  const [swiperUpdate, setSwiperUpdate] = useState<{
    id: string;
    newItem: DayCardItem;
  } | null>(null);
  const swiperRef = useRef<InfiniteSwiperRef>(null);
  const [isTouch, setIsTouch] = useState(() => {
    if (typeof window === "undefined") return false;
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
  });

  // Add a ref to always get the latest menu state
  const menuRef = useRef<DailyMenu | null>(null);
  useEffect(() => {
    menuRef.current = menu;
  }, [menu]);

  // Animation state
  const [animatingDirection, setAnimatingDirection] = useState<
    "left" | "right" | null
  >(null);

  useEffect(() => {
    setIsAnyModalOpen(showModalRecipeId != undefined);
  }, [showModalRecipeId, setIsAnyModalOpen]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [categoriesResp, allergenResp, ingredientResp] =
          await Promise.all([
            getCategories(),
            getAllergens(),
            getIngredients(),
          ]);
        setCategories(categoriesResp.data.data);
        setIngredients(ingredientResp.data.data);
        setAllergens(allergenResp.data.data);
      } catch (err: any) {
        handleApiError(err, "feil ved henting av ressurser");
      } finally {
      }
    };
    fetchAll();
  }, []);

  const setEditingForDate = (date: string, value: boolean) => {
    setEditingMap((prev) => {
      const newMap: Record<string, boolean> = {};
      for (const key of Object.keys(prev)) {
        newMap[key] = false; // only one editable at a time
      }
      newMap[date] = value;
      return newMap;
    });
    if (!value) {
      setTempMenu(null);
    }
  };

  // --- Touch capability listener (optional dynamic detection) ---
  useEffect(() => {
    const checkTouch = () =>
      setIsTouch("ontouchstart" in window || navigator.maxTouchPoints > 0);

    checkTouch();

    // Some browsers might change touch support (rare)
    window.addEventListener("resize", checkTouch);
    window.addEventListener("orientationchange", checkTouch);

    return () => {
      window.removeEventListener("resize", checkTouch);
      window.removeEventListener("orientationchange", checkTouch);
    };
  }, []);

  // --- Data loading (current day only; mobile swiper fetches others on demand) ---
  const fetchMenus = useCallback(async () => {
    if (!selectedFontenehus) return;
    if (isTouch) return;
    setError(null);

    const safeFetch = async (date: string) => {
      try {
        const response = await getDailyMenuByDate(date);

        if ((response as any)?.status === 204) return null;
        return response.data.data;
      } catch (err: any) {
        handleApiError(err, "En feil oppsto ved lasting av dagsmeny.");
      }
    };

    try {
      const dailyMenu = await safeFetch(formatLocalDate(selectedDate));
      setMenu(dailyMenu);
    } catch (err) {
      handleApiError(err, "En feil oppsto ved lasting av dagsmeny.");
      setMenu(null);
    }
  }, [selectedFontenehus, refreshTrigger, getDailyMenuByDate, selectedDate]);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  useEffect(() => {
    const loadAllRecipes = async () => {
      try {
        const recipeResponse = await getRecipes();

        const result = recipeResponse.data.data;
        setAllRecipes(result);
      } catch (err: any) {
        handleApiError(err, "En feil oppsto ved lasting av oppskrifter.");
      }
    };
    loadAllRecipes();
  }, [refreshKey]);

  // --- Navigation ---
  const goToNextDay = useCallback(async () => {
    if (animatingDirection) return;
    if (!isTouch) if (animatingDirection) return;

    if (!isTouch && editing) {
      const shouldDiscard = await confirm({
        title: "Ulagrede endringer",
        message: "Du har ulagrede endringer. Vil du forkaste dem?",
        confirmText: "Forkast",
        cancelText: "Avbryt",
      });

      if (!shouldDiscard) return;
      setTempMenu(null);
      setEditing(false);
    }
    setAnimatingDirection("left");
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
    setTimeout(() => {
      setSelectedDate(newDate);
      setAnimatingDirection(null);
    }, ANIMATION_DURATION);
    if (isTouch) {
      swiperRef.current?.slideNext();
    }
  }, [selectedDate, animatingDirection, editing]);

  const goToPreviousDay = useCallback(async () => {
    if (animatingDirection) return;
    if (!isTouch && editing) {
      const shouldDiscard = await confirm({
        title: "Ulagrede endringer",
        message: "Du har ulagrede endringer. Vil du forkaste dem?",
        confirmText: "Forkast",
        cancelText: "Avbryt",
      });

      if (!shouldDiscard) return;
      setTempMenu(null);
      setEditing(false);
    }

    setAnimatingDirection("right");
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
    setTimeout(() => {
      setSelectedDate(newDate);
      setAnimatingDirection(null);
    }, ANIMATION_DURATION);
    if (isTouch) {
      swiperRef.current?.slidePrev();
    }
  }, [selectedDate, animatingDirection, editing]);

  const handleEdit = (dateKey: string, menu: DailyMenu | null) => {
    if (menu) {
      setTempMenu(menu);
      if (isTouch) setEditingForDate(dateKey, true);
      else {
        setEditing(true);
      }
    }
  };

  const handleCancel = (dateKey: string) => {
    if (isTouch) {
      setEditingForDate(dateKey, false);
    } else {
      setEditing(false);
    }

    setTempMenu(null);
  };

  const handleSave = async (dateKey: string) => {
    if (!tempMenu) return;
    try {
      const dto = buildUpdateDto(
        tempMenu,
        (tempMenu.recipes ?? []).map((r) => r.id),
      );
      const response = await updateDailyMenu(dateKey, dto);
      const updated = response.data.data;
      setMenu(updated);
      setTempMenu(null);

      if (isTouch) {
        setEditingForDate(dateKey, false);
        const updatedItem = { id: dateKey, date: dateKey, menu: updated };
        setSwiperUpdate({ id: updatedItem.id, newItem: updatedItem }); // ✅ triggers externalUpdate
      } else {
        setEditing(false);
      }
    } catch (err: any) {
      handleApiError(err, "En feil oppsto ved lagring av dagsmeny.");
    }
  };

  const handleChange = (field: keyof DailyMenu, value: string | number) => {
    if (tempMenu) setTempMenu({ ...tempMenu, [field]: value } as DailyMenu);
  };

  const handleSlideChange = (newItem: DayCardItem, prevItem: DayCardItem) => {
    if (!tempMenu) return true; // no unsaved changes

    const direction = newItem.id > prevItem.id ? "next" : "prev";

    confirm({
      title: "Ulagrede endringer",
      message: "Du har ulagrede endringer. Vil du forkaste dem?",
      confirmText: "Forkast",
      cancelText: "Avbryt",
    }).then((ok) => {
      if (ok) {
        setTempMenu(null);
        if (direction === "next") swiperRef.current?.slideNext();
        else swiperRef.current?.slidePrev();
      }
    });

    return false; // always block the original slide
  };

  const handleAddRecipe = useCallback(
    async (
      dateStr: string,
      alertUser: boolean,
      recipeIdFromSuggestion?: number,
    ) => {
      const dateKey = dateStr || formatLocalDate(selectedDate);
      const recipeId = recipeIdFromSuggestion ?? selectedRecipeIds[dateKey];

      const recipe = allRecipes.find((r) => r.id === recipeId);

      if (alertUser) {
        const ok = await confirm({
          title: "Legg til oppskrift",
          message: `Er du sikker på at du vil legge til "${recipe?.title}"?`,
          confirmText: "Legg til",
        });
        if (!ok) return;
      }

      if (!recipeId) {
        setError("Velg en oppskrift å legge til");
        return;
      }
      if (!selectedFontenehus) {
        setError("Ingen fontenehus valgt");
        return;
      }

      try {
        const currentMenu = menuRef.current;
        const current = currentMenu
          ? { ...currentMenu, recipes: [...(currentMenu.recipes ?? [])] }
          : {
              id: 0,
              fontenehusId: selectedFontenehus.id,
              date: dateKey,
              peopleAte: 0,
              portionsLeft: 0,
              suggestedBy: user?.username ?? "",
              notes: "",
              recipes: [],
              comments: [],
            };

        // Detect if already exists
        const alreadyExists = current.recipes.some((r) => r.id === recipeId);

        if (alreadyExists) {
          toast.error(`"${recipe?.title}" ligger allerede i menyen.`);
          return;
        }

        // Proceed to actually add the recipe
        const newIds = [...current.recipes.map((r) => r.id), recipeId];

        const dto = buildUpdateDto(current, newIds);
        const response = await updateDailyMenu(dateKey, dto);
        const updated = response.data.data;

        toast.success(
          `Oppskriften "${recipe?.title}" er lagt til i dagsmenyen!`,
        );
        setMenu(updated);
        setSelectedRecipeIds((prev) => ({ ...prev, [dateKey]: "" }));
        setTempMenu(null);
        setError(null);

        if (isTouch) {
          const updatedItem = { id: dateKey, date: dateKey, menu: updated };
          setSwiperUpdate({ id: updatedItem.id, newItem: updatedItem });
        }
      } catch (err) {
        handleApiError(err, "En feil oppsto ved lagring av dagsmeny.");
      }
    },
    [
      menu,
      selectedDate,
      selectedFontenehus,
      user?.username,
      selectedRecipeIds,
      isTouch,
      updateDailyMenu,
      buildUpdateDto,
      formatLocalDate,
    ],
  );

  const handleDeleteDailyMenuRecipe = async (recipeId: number) => {
    if (!menuRef.current) return;
    const ok = await confirm({
      title: "Fjern oppskrift",
      message:
        "Er du sikker på at du vil fjerne denne oppskriften fra dagens meny?",
      confirmText: "Fjern",
    });

    if (!ok) return;

    try {
      await deleteDailyMenuRecipe(menuRef.current.id, recipeId);
      // ✅ Force a fresh immutable copy so React detects the change
      // ✅ Properly update the state to remove the deleted recipe
      setMenu((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          recipes: prev.recipes?.filter((r) => r.id !== recipeId) ?? [],
        };
      });

      // ✅ Also update tempMenu if it exists and contains the same recipe
      setTempMenu((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          recipes: prev.recipes?.filter((r) => r.id !== recipeId) ?? [],
        };
      });

      // ✅ Trigger swiper card rerender
      if (isTouch && menuRef.current) {
        const updatedItem = {
          id: menuRef.current.date,
          date: menuRef.current.date,
          menu: {
            ...menuRef.current,
            recipes: menuRef.current.recipes.filter((r) => r.id !== recipeId),
          },
        };
        setSwiperUpdate({ id: updatedItem.id, newItem: updatedItem });
      }
      toast.success("Oppskriften er fjernet fra dagens meny!");
    } catch (err: any) {
      handleApiError(err, "Feil ved sletting av oppskrift fra dagsmeny.");
    }
  };

  const canAddRecipe = user?.roles?.some((r) =>
    ["MenuResponsible"].includes(r),
  );

  const isAfterNextFriday = (dateStr: string) => {
    const today = new Date();
    const [y, m, d] = dateStr.split("-").map(Number);
    const check = new Date(y, m - 1, d);

    // Current day of week (0=Sun, 1=Mon, ..., 5=Fri, 6=Sat)
    const dayOfWeek = today.getDay();

    // Days until next Friday
    // If today is Friday (5), next Friday is 7 days away
    const daysUntilFriday = (5 - dayOfWeek + 7) % 7 || 7;

    const nextFriday = new Date(today);
    nextFriday.setDate(today.getDate() + daysUntilFriday);

    // Return false if check date is before next Friday
    return check >= nextFriday;
  };

  const isFutureLunch = (dateStr: string): boolean => {
    const [y, m, d] = dateStr.split("-").map(Number);

    const lunchTime = new Date(y, m - 1, d, 12, 15, 0, 0);
    const now = new Date();

    return lunchTime >= now;
  };

  // --- Render helpers ---
  const formattedDate = (() => {
    const formatted = selectedDate.toLocaleDateString("no-NO", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  })();

  // --- Memoized renderCardContent ---
  const renderCardContent = useCallback(
    (
      menu: DailyMenu | null,
      dateKey: string,
      isEditingProp?: boolean,
      _setEditingForDate?: (date: string, value: boolean) => void,
    ) => {
      const isEditing = isEditingProp ?? false;

      const menuRecipeIds = menu?.recipes?.map((r) => r.id) || [];

      const showNoMenuMessage = !menu?.recipes || menu.recipes.length === 0;

      return (
        <>
          <div className="relative w-full no-bounce">
            {!!showModalRecipeId &&
              dateKey === formatLocalDate(selectedDate) && (
                <RecipeDetail
                  peopleMadeFor={menu?.peopleAte}
                  isOpen={
                    !!showModalRecipeId &&
                    dateKey === formatLocalDate(selectedDate)
                  }
                  isModal
                  recipeId={showModalRecipeId}
                  onClose={() => setShowModalRecipeId(undefined)}
                  onRecipeUpdate={(updatedRecipe) => {
                    fetchMenus();
                    // Update your local state with the new recipe data
                    setAllRecipes((prev) =>
                      prev.map((r) =>
                        r.id === updatedRecipe.id ? updatedRecipe : r,
                      ),
                    );
                  }}
                />
              )}

            <div ref={containerRef} className="w-full content-height-fits">
              {isAfterNextFriday(dateKey) && (
                <SuggestedLunches
                  date={dateKey}
                  allRecipes={allRecipes}
                  currentUser={user?.username || "Ukjent"}
                  recipeCategories={categories}
                  allergensList={allergens}
                  ingredientsList={ingredients}
                  setModalRecipeId={setShowModalRecipeId}
                  onAddRecipeFromSuggestion={(date, recipeId) =>
                    handleAddRecipe(date, true, recipeId)
                  }
                />
              )}
              <div className="bg-white/90 backdrop-blur-md rounded-2xl p-4 mt-4">
                <h2 className="text-xl font-bold text-orange-600 mb-3 text-center">
                  🍽️ Dagens meny
                </h2>

                {menuRecipeIds.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    <RecipeList
                      recipes={menu?.recipes ?? []}
                      onDelete={handleDeleteDailyMenuRecipe}
                      setModalRecipeId={setShowModalRecipeId}
                    />
                  </div>
                ) : (
                  <p className="text-gray-500 text-center">
                    Ingen oppskrifter i dagens meny ennå.
                  </p>
                )}
              </div>
              {/* Add recipe section */}
              {canAddRecipe && (
                <AddRecipeSection
                  allRecipes={allRecipes}
                  menuRecipeIds={menuRecipeIds}
                  selectedRecipeId={selectedRecipeIds[dateKey] ?? ""}
                  setSelectedRecipeId={(id) =>
                    setSelectedRecipeIds((prev) => ({ ...prev, [dateKey]: id }))
                  }
                  onAddRecipe={() => handleAddRecipe(dateKey, false)}
                  buttonLabel={
                    showNoMenuMessage ? "➕ Opprett dagsmeny" : undefined
                  }
                  recipeCategories={categories}
                  allergensList={allergens}
                  ingredientsList={ingredients}
                />
              )}

              {/* Menu details and actions if menu exists */}
              {menu && menuRecipeIds.length > 0 && (
                <>
                  <MenuDetails
                    isFutureLunch={isFutureLunch(dateKey)}
                    editing={isEditing}
                    menu={menu}
                    tempMenu={tempMenu}
                    onChange={handleChange}
                  />
                  {canAddRecipe && (
                    <ActionButtons
                      editing={isEditing}
                      onEdit={() => handleEdit(dateKey, menu)}
                      onSave={() => handleSave(dateKey)}
                      onCancel={() => handleCancel(dateKey)}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </>
      );
    },
    [
      allRecipes,
      selectedRecipeIds,
      tempMenu,
      user?.username,
      showModalRecipeId,
    ],
  );

  const fetchPage = useCallback(
    async (opts: {
      after?: string;
      before?: string;
      limit?: number;
    }): Promise<{
      items: DayCardItem[];
      nextCursor?: string | null;
      prevCursor?: string | null;
    }> => {
      const { after, before, limit = 8 } = opts;

      const base = after
        ? new Date(after)
        : before
          ? new Date(before)
          : new Date(selectedDate);

      // Compute date range (inclusive)
      const start = new Date(base);
      const end = new Date(base);

      if (after) {
        // When swiping right → load next days
        end.setDate(end.getDate() + (limit - 1));
      } else if (before) {
        // When swiping left → load previous days
        start.setDate(start.getDate() - (limit - 1));
        end.setDate(end.getDate());
      } else {
        // ✅ Initial load: center around base date
        const half = Math.floor(limit / 2);
        start.setDate(start.getDate() - half);
        end.setDate(end.getDate() + (limit - 1 - half));
      }

      const startStr = formatLocalDate(start);
      const endStr = formatLocalDate(end);

      try {
        const response = await getDailyMenusByRange(startStr, endStr);
        const menus: DailyMenu[] = response.data.data;

        // Convert results to DayCardItem[]
        const items: DayCardItem[] = [];
        for (let i = 0; i < limit; i++) {
          const d = new Date(start);
          d.setDate(d.getDate() + i);
          const ds = formatLocalDate(d);
          const m = menus.find((x) => x.date === ds) || null;
          if (m?.date === formatLocalDate(selectedDate)) {
            setMenu(m);
          }
          items.push({ id: ds, date: ds, menu: m });
        }

        const first = new Date(items[0].date);
        const last = new Date(items[items.length - 1].date);
        const nextCursor = formatLocalDate(
          new Date(last.getTime() + 24 * 60 * 60 * 1000),
        );
        const prevCursor = formatLocalDate(
          new Date(first.getTime() - 24 * 60 * 60 * 1000),
        );

        return { items, nextCursor, prevCursor };
      } catch (err: any) {
        handleApiError(err, "En feil oppsto ved lasting av dagsmenyer.");
        return { items: [], nextCursor: null, prevCursor: null };
      }
    },
    [getDailyMenusByRange],
  );

  const renderCard = (item: DayCardItem) => {
    const isEditing = editingMap[item.date] || false;

    return (
      <div
        key={item.date}
        className="day-card mobile-view max-w-xl mx-auto px-4 min-h-[600px]"
      >
        {renderCardContent(item.menu, item.date, isEditing)}
      </div>
    );
  };

  // -----------------------------------------------------

  return (
    <div className="day-details-wrapper bg-orange-50 pt-16 pb-20 relative">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 mx-4">
          {error}
        </div>
      )}

      {/* ✅ Fixed (non-scrolling) header */}
      <div className="sticky top-0 z-30 bg-orange-50/80 backdrop-blur-md border-b border-orange-200">
        <DayNavigation
          formattedDate={formattedDate}
          goToPreviousDay={goToPreviousDay}
          goToNextDay={goToNextDay}
        />
      </div>

      {/* ✅ Scrollable content area */}
      {isTouch ? (
        <div className="mt-4">
          <InfiniteSwiper<DayCardItem>
            ref={swiperRef}
            initialPageSize={8}
            fetchPage={fetchPage}
            renderCard={renderCard}
            maxBuffer={40}
            prefetchThreshold={2}
            initialSlideIndex={4} // Center on the 5th item (today)
            onBeforeSlideChange={handleSlideChange}
            onActiveIndexChange={(activeItem) => {
              setSelectedDate(new Date(activeItem.date));
              setMenu(activeItem.menu);
              setTempMenu(null);
              setEditingMap({}); // ✅ clear editing for all cards
            }}
            externalUpdate={swiperUpdate}
          />
        </div>
      ) : (
        <div className="day-card desktop-view max-w-2xl mx-auto mt-4">
          {renderCardContent(menu, formatLocalDate(selectedDate), editing)}
        </div>
      )}
    </div>
  );
};

export default DayDetailsPage;
